import React, { useEffect, useId, useRef, useState } from 'react';
import { CircleX, Info, RotateCcw } from 'lucide-react';
import { validatePermission } from 'actions';
import { cooperplaceApi } from 'services/api';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { formatDate } from 'v3/utils/controlTowerDate';
import { canReadCandidates, linkLabel, platesLabel } from '../Candidates/contract';
import { assetsOf, reasonsResponse, detailsResponse, mutationResponse } from './contract';
import {
  refusalActorKey,
  operationKey,
  readOperation,
  beginOperation,
  clearOperation,
} from './operation';
import useCurrentCompanyId from '../useCurrentCompanyId';

/**
 * variant="actions": Recusar ou, com recusa ativa, só o ícone Recusado (confirma e reverte).
 * variant="plate": ícone acima da placa quando há recusa nesta carga (ativa ou revertida).
 */
export default function RefusalActions({
  candidate,
  context,
  disabled,
  onOpenChange = () => {},
  onChanged,
  variant = 'actions',
}) {
  const reasonId = useId();
  const companyId = useCurrentCompanyId();
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;
  const [mode, setMode] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState([]);
  // Recusas ativas que o Sim reverte (uma pelo ⓘ da placa; todas do candidato pelo ícone Recusado).
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(false);
  const generation = useRef(0);
  const pending = useRef(false);
  const writing = useRef(false);
  const readable = companyId === context.companyId && canReadCandidates(validatePermission);
  const writable = readable && validatePermission('ATENDER_CARGA');
  const canRefuse =
    writable && candidate.profileEvaluation === 'COMPATIBLE' && !candidate.refusal.active;
  const hasHistory =
    candidate.refusal.active || candidate.plateHistory?.some((h) => h.refusals?.length);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  useEffect(() => {
    if (!readable) {
      generation.current += 1;
      pending.current = false;
      writing.current = false;
      setMode(null);
      setBusy(false);
      onOpenChange(false);
    }
  }, [readable, onOpenChange]);
  const close = () => {
    if (writing.current) return;
    generation.current += 1;
    pending.current = false;
    setMode(null);
    setBusy(false);
    setError(null);
    onOpenChange(false);
  };
  async function run(action) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const version = ++generation.current;
    try {
      const actor = refusalActorKey(context);
      const current = () =>
        generation.current === version &&
        companyIdRef.current === context.companyId &&
        refusalActorKey(context) === actor;
      await action(current);
    } catch (failure) {
      if (generation.current === version)
        setError(
          failure?.response?.data?.message ||
            failure.message ||
            'Não foi possível concluir a ação.',
        );
    } finally {
      if (generation.current === version) {
        pending.current = false;
        writing.current = false;
        setBusy(false);
      }
    }
  }
  const openRefusal = () => {
    if (!canRefuse || disabled) return;
    setMode('refuse');
    setReasons([]);
    setReason('');
    setRetry(false);
    onOpenChange(true);
    run(async (current) => {
      const response = await cooperplaceApi.get('/control-tower/refusal-reasons');
      if (!current()) return;
      const list = reasonsResponse(response.data);
      const previous = readOperation(operationKey(context, candidate.vehicle));
      setReasons(list);
      setReason(previous?.reasonCode || '');
      setRetry(Boolean(previous));
    });
  };
  const openDetails = () => {
    if (!readable || disabled) return;
    setMode('details');
    setDetails([]);
    setTargets([]);
    setRetry(false);
    onOpenChange(true);
    run(async (current) => {
      const result = await Promise.all(
        assetsOf(candidate).map(async (asset) => {
          const { data } = await cooperplaceApi.get(
            `/control-tower/loads/${context.item.loadId}/plates/${asset.assetType}/${asset.id}/refusal`,
          );
          return detailsResponse(data, context, asset);
        }),
      );
      if (current()) setDetails(result);
    });
  };
  const reverse = (detail) => {
    if (!writable || busy || !detail.active) return;
    setTargets([detail]);
    setMode('reverse');
    setError(null);
    setRetry(false);
  };
  // Ícone Recusado: pede só a confirmação e reverte as recusas ativas do candidato, sem passar
  // pelos dados da recusa.
  const openReversal = () => {
    if (!writable || disabled) return;
    setMode('reverse');
    setTargets([]);
    setRetry(false);
    onOpenChange(true);
    run(async (current) => {
      const result = await Promise.all(
        assetsOf(candidate).map(async (asset) => {
          const { data } = await cooperplaceApi.get(
            `/control-tower/loads/${context.item.loadId}/plates/${asset.assetType}/${asset.id}/refusal`,
          );
          return detailsResponse(data, context, asset);
        }),
      );
      if (!current()) return;
      const active = result.filter((detail) => detail.active);
      if (!active.length) throw new Error('Nenhuma recusa ativa para reverter. Atualize a fila.');
      setTargets(active);
    });
  };
  const submit = () => {
    if (
      !writable ||
      disabled ||
      pending.current ||
      (mode === 'refuse' &&
        (!canRefuse || !reason || (!retry && !reasons.some((r) => r.code === reason))))
    )
      return;
    const reversal = mode === 'reverse';
    const items = reversal
      ? targets.filter((t) => t.active).map((t) => ({ asset: t.asset, refusalId: t.event.id }))
      : [{ asset: candidate.vehicle, refusalId: null }];
    if (!items.length || items.some((item) => !item.asset)) return;
    run(async (current) => {
      writing.current = true;
      setRetry(true);
      let replayed = false;
      for (const { asset, refusalId } of items) {
        const key = operationKey(context, asset, refusalId);
        const command = beginOperation(
          key,
          reversal ? { confirmed: true } : { reasonCode: reason },
        );
        const url = reversal
          ? `/control-tower/loads/${context.item.loadId}/plates/${asset.assetType}/${asset.id}/refusals/${refusalId}/reversal`
          : `/control-tower/loads/${context.item.loadId}/candidates/${candidate.vehicleId}/refusals`;
        let response;
        try {
          response = await cooperplaceApi.post(url, command);
        } catch (failure) {
          // Erros definitivos de validação/autorização não mantêm uma ação ambígua pendente.
          if ([401, 403, 404, 409, 422].includes(failure.response?.status)) {
            clearOperation(key);
            if (current()) setRetry(false);
          }
          throw failure;
        }
        const result = mutationResponse(response.data, context, asset, { ...command, refusalId });
        clearOperation(key);
        replayed = replayed || result.replayed;
      }
      if (!current()) return;
      const plates = items.map((item) => item.asset.plate).join(', ');
      const message = reversal
        ? `Reversão confirmada para ${plates}.`
        : `Recusa confirmada para ${plates}.`;
      const state = replayed ? ' Operação já registrada; consultando o estado atual.' : '';
      writing.current = false;
      close();
      onChanged(message + state);
    });
  };
  const content =
    mode === 'refuse' ? (
      <>
        <p>
          Confirme a recusa do veículo <strong>{candidate.vehicle.plate}</strong> para esta carga.
        </p>
        <label htmlFor={reasonId}>Motivo da recusa *</label>
        <select
          id={reasonId}
          className="form-select"
          value={reason}
          disabled={busy || retry}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">Selecione o motivo</option>
          {reasons.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
          {retry && !reasons.some((r) => r.code === reason) && (
            <option value={reason}>{reason}</option>
          )}
        </select>
        {!busy && !error && !reasons.length && (
          <p>Nenhum motivo disponível. Atualize a consulta.</p>
        )}
      </>
    ) : mode === 'reverse' ? (
      targets.length > 0 && (
        <>
          <p>
            <strong>Deseja realmente reverter esta ação?</strong>
          </p>
          {targets.map((target) => (
            <p key={target.event.id}>
              {target.asset.assetType === 'wagon' ? 'Carreta' : 'Cavalo'} {target.asset.plate} ·
              Motivo: {target.event.refusalReason.text} · Recusa registrada por{' '}
              {target.event.actor.name} em {formatDate(target.event.occurredAt)} · Brasília.
            </p>
          ))}
          <p>A fila será recalculada conforme a prioridade e a disponibilidade atuais.</p>
        </>
      )
    ) : (
      details.map((detail) => (
        <section key={`${detail.asset.assetType}:${detail.asset.id}`} className="mb-3">
          <h3 className="h6">
            {detail.asset.assetType === 'wagon' ? 'Carreta' : 'Cavalo'} {detail.asset.plate}
          </h3>
          {!detail.event ? (
            <p>Nenhuma recusa registrada para esta carga e placa.</p>
          ) : (
            <>
              <dl className="tc-refusal-details">
                <dt>Situação</dt>
                <dd>{detail.active ? 'Recusa ativa' : 'Recusa revertida'}</dd>
                <dt>Motivo</dt>
                <dd>{detail.event.refusalReason.text}</dd>
                <dt>Usuário</dt>
                <dd>{detail.event.actor.name}</dd>
                <dt>Data e hora</dt>
                <dd>{formatDate(detail.event.occurredAt)} · Brasília</dd>
                <dt>Vínculo na recusa</dt>
                <dd>{linkLabel(detail.event)}</dd>
              </dl>
              {detail.active && (
                <Button
                  size="sm"
                  disabled={!writable || busy}
                  onClick={() => reverse(detail)}
                  aria-label={`Reverter recusa de ${detail.asset.plate}`}
                >
                  Reverter recusa
                </Button>
              )}
            </>
          )}
        </section>
      ))
    );
  if (variant === 'plate' && !hasHistory) return null;
  const trigger =
    variant === 'plate' ? (
      <button
        type="button"
        className="tc-c-refusal-info"
        disabled={disabled || !readable}
        onClick={(event) => {
          event.stopPropagation();
          openDetails();
        }}
        aria-label={`Ver dados da recusa de ${platesLabel(candidate)}`}
        title="Ver dados da recusa"
      >
        <Info size={16} aria-hidden="true" />
      </button>
    ) : candidate.refusal.active ? (
      <button
        type="button"
        className="tc-c-refused-action"
        disabled={disabled || !readable}
        onClick={writable ? openReversal : openDetails}
        aria-label={`Recusado: reverter a recusa de ${platesLabel(candidate)}`}
        title={
          writable
            ? 'Recusado nesta carga. Reverter a recusa'
            : 'Recusado nesta carga. Ver dados da recusa'
        }
      >
        <RotateCcw size={16} aria-hidden="true" />
        <span>Recusado</span>
      </button>
    ) : (
      <button
        type="button"
        className="tc-c-refuse"
        disabled={disabled || !canRefuse}
        onClick={openRefusal}
        aria-label={`Recusar ${platesLabel(candidate)}`}
        title="Recusar veículo nesta carga"
      >
        <CircleX size={18} aria-hidden="true" />
      </button>
    );
  return (
    <>
      {trigger}
      <Modal
        show={Boolean(mode) && readable}
        handleClose={close}
        closeButton={!writing.current}
        backdrop={writing.current ? 'static' : true}
        keyboard={!writing.current}
        heading={
          mode === 'refuse'
            ? 'Recusar veículo'
            : mode === 'reverse'
              ? 'Reverter recusa'
              : 'Dados da recusa'
        }
        body={
          <div className="tc-refusal-form">
            <p>
              <strong>
                {context.item.type === 'PRE_LOAD' ? 'Pré-carga' : 'Carga'} {context.item.loadId}
              </strong>{' '}
              · {platesLabel(candidate)}
            </p>
            {content}
            {busy && <p role="status">{writing.current ? 'Registrando…' : 'Consultando…'}</p>}
            {retry && !busy && (
              <p>Confirme novamente para verificar a mesma operação após a falha de comunicação.</p>
            )}
            {error && (
              <div role="alert">
                <p>{error}</p>
                <Button
                  size="sm"
                  onClick={() => {
                    close();
                    onChanged();
                  }}
                >
                  Atualizar fila
                </Button>
              </div>
            )}
          </div>
        }
        footer={
          <>
            <Button variant="secondary" disabled={writing.current} onClick={close}>
              {mode === 'reverse' ? 'Não' : 'Cancelar'}
            </Button>
            {mode !== 'details' && (
              <Button
                disabled={
                  busy ||
                  !writable ||
                  (mode === 'refuse' && !reason) ||
                  (mode === 'reverse' && !targets.length)
                }
                onClick={submit}
              >
                {mode === 'reverse' ? 'Sim' : 'Confirmar recusa'}
              </Button>
            )}
          </>
        }
      />
    </>
  );
}

/** Ícone acima da placa: dados da recusa desta carga, quando houver. */
export function RefusalPlateInfo(props) {
  return <RefusalActions {...props} variant="plate" />;
}
